/**
 * Onboarding Wizard — shown after first login for new tenants.
 * 4 steps: Firm Info → Team → Connect Xero → Clients
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import apiClient from '@/services/api/client';
import { toast } from 'sonner';
import {
  Building2, Users, Link2, FolderOpen, Check, ChevronRight, ChevronLeft,
  Sparkles, Upload, Plus, Trash2, Loader2, Globe, Clock, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

const STEPS = [
  { id: 'firm', label: 'Your Firm', icon: Building2, description: 'Tell us about your practice' },
  { id: 'team', label: 'Your Team', icon: Users, description: 'Invite your team members' },
  { id: 'xero', label: 'Connect Xero', icon: Link2, description: 'Link your Xero account' },
  { id: 'clients', label: 'Your Clients', icon: FolderOpen, description: 'Import or add clients' },
];

const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'UTC', label: 'UTC' },
];

// ==================== Step 1: Firm Info ====================

function StepFirm({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="abn">ABN</Label>
          <Input
            id="abn"
            value={data.abn || ''}
            onChange={(e) => onChange({ ...data, abn: e.target.value })}
            placeholder="e.g. 48 654 068 039"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="acn">ACN</Label>
          <Input
            id="acn"
            value={data.acn || ''}
            onChange={(e) => onChange({ ...data, acn: e.target.value })}
            placeholder="e.g. 654 068 039"
            className="mt-1"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={data.phone || ''}
            onChange={(e) => onChange({ ...data, phone: e.target.value })}
            placeholder="e.g. 08 1234 5678"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            value={data.website || ''}
            onChange={(e) => onChange({ ...data, website: e.target.value })}
            placeholder="e.g. www.yourfirm.com.au"
            className="mt-1"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Timezone</Label>
          <Select value={data.timezone || 'Australia/Sydney'} onValueChange={(v) => onChange({ ...data, timezone: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Financial Year End</Label>
          <Select value={data.fyEndMonth || '6'} onValueChange={(v) => onChange({ ...data, fyEndMonth: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="6">June (Australian standard)</SelectItem>
              <SelectItem value="12">December</SelectItem>
              <SelectItem value="3">March</SelectItem>
              <SelectItem value="9">September</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="address">Address</Label>
        <Input
          id="address"
          value={data.address || ''}
          onChange={(e) => onChange({ ...data, address: e.target.value })}
          placeholder="e.g. 123 Main St, Adelaide SA 5000"
          className="mt-1"
        />
      </div>
    </div>
  );
}

// ==================== Step 2: Team ====================

function StepTeam({ invites, setInvites }: { invites: Array<{ email: string; role: string }>; setInvites: (i: any) => void }) {
  const addInvite = () => setInvites([...invites, { email: '', role: 'user' }]);
  const removeInvite = (idx: number) => setInvites(invites.filter((_: any, i: number) => i !== idx));
  const updateInvite = (idx: number, field: string, value: string) => {
    const updated = [...invites];
    (updated[idx] as any)[field] = value;
    setInvites(updated);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Invite your team members. They'll receive an email with login instructions. You can always add more later.
      </p>

      {invites.map((inv, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <Input
            value={inv.email}
            onChange={(e) => updateInvite(idx, 'email', e.target.value)}
            placeholder="team@yourfirm.com.au"
            className="flex-1"
            type="email"
          />
          <Select value={inv.role} onValueChange={(v) => updateInvite(idx, 'role', v)}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tenant_admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="user">Accountant</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={() => removeInvite(idx)} className="shrink-0">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}

      <Button variant="outline" size="sm" onClick={addInvite} className="w-full">
        <Plus className="h-4 w-4 mr-2" /> Add Team Member
      </Button>
    </div>
  );
}

// ==================== Step 3: Xero ====================

function StepXero({ xeroStatus, onConnect }: { xeroStatus: any; onConnect: () => void }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Connect your Xero account to automatically sync financial data for your clients.
        RoniDoc uses read-only access — we can never modify your Xero data.
      </p>

      {xeroStatus?.connected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-600">
            <Check className="h-5 w-5" />
            <span className="font-medium">Connected to Xero</span>
          </div>
          {xeroStatus.organizations?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Connected Organizations ({xeroStatus.organizations.length}):</p>
              {xeroStatus.organizations.map((org: any) => (
                <div key={org.xero_tenant_id} className="flex items-center gap-2 rounded-md border p-2.5 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{org.xero_tenant_name}</span>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={onConnect}>
            <Plus className="h-4 w-4 mr-1" /> Add More Organizations
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center py-8 space-y-4">
          <div className="h-16 w-16 rounded-2xl bg-[#13B5EA]/10 flex items-center justify-center">
            <Link2 className="h-8 w-8 text-[#13B5EA]" />
          </div>
          <Button onClick={onConnect} size="lg" className="bg-[#13B5EA] hover:bg-[#0DA5D7]">
            Connect to Xero
          </Button>
          <p className="text-xs text-muted-foreground">You'll be redirected to Xero to authorize access</p>
        </div>
      )}
    </div>
  );
}

// ==================== Step 4: Clients ====================

function StepClients({ clients, newClients, setNewClients }: {
  clients: any[];
  newClients: Array<{ name: string; abn: string }>;
  setNewClients: (c: any) => void;
}) {
  const addClient = () => setNewClients([...newClients, { name: '', abn: '' }]);
  const removeClient = (idx: number) => setNewClients(newClients.filter((_: any, i: number) => i !== idx));
  const updateClient = (idx: number, field: string, value: string) => {
    const updated = [...newClients];
    (updated[idx] as any)[field] = value;
    setNewClients(updated);
  };

  return (
    <div className="space-y-4">
      {clients.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-green-600">
            <Check className="inline h-4 w-4 mr-1" />
            {clients.length} clients imported from Xero
          </p>
          <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
            {clients.map((c: any) => (
              <div key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{c.display_name || c.displayName}</span>
                {(c.xero_org_name || c.xeroOrgName) && (
                  <span className="text-xs text-muted-foreground ml-auto truncate">Xero: {c.xero_org_name || c.xeroOrgName}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        {clients.length > 0 ? 'Add more clients manually, or skip to finish.' : 'Add your clients manually, or connect Xero first to auto-import.'}
      </p>

      {newClients.map((c, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <Input
            value={c.name}
            onChange={(e) => updateClient(idx, 'name', e.target.value)}
            placeholder="Client name"
            className="flex-1"
          />
          <Input
            value={c.abn}
            onChange={(e) => updateClient(idx, 'abn', e.target.value)}
            placeholder="ABN (optional)"
            className="w-[160px]"
          />
          <Button variant="ghost" size="icon" onClick={() => removeClient(idx)} className="shrink-0">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}

      <Button variant="outline" size="sm" onClick={addClient} className="w-full">
        <Plus className="h-4 w-4 mr-2" /> Add Client
      </Button>
    </div>
  );
}

// ==================== Main Wizard ====================

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [firmData, setFirmData] = useState<any>({ timezone: 'Australia/Sydney', fyEndMonth: '6' });
  const [invites, setInvites] = useState<Array<{ email: string; role: string }>>([{ email: '', role: 'user' }]);
  const [xeroStatus, setXeroStatus] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [newClients, setNewClients] = useState<Array<{ name: string; abn: string }>>([]);

  // Load existing tenant data
  useEffect(() => {
    loadTenantData();
    loadXeroStatus();
    loadClients();
  }, []);

  const loadTenantData = async () => {
    try {
      const res = await apiClient.get('/tenants/current');
      const tenant = res.data?.data;
      if (tenant) {
        setFirmData({
          name: tenant.name || '',
          abn: tenant.settings?.abn || '',
          acn: tenant.settings?.acn || '',
          phone: tenant.settings?.phone || '',
          website: tenant.settings?.website || '',
          timezone: tenant.settings?.timezone || 'Australia/Sydney',
          fyEndMonth: tenant.settings?.fyEndMonth || '6',
          address: tenant.settings?.address || '',
        });
      }
    } catch { /* ignore */ }
  };

  const loadXeroStatus = async () => {
    try {
      const res = await apiClient.get('/integrations/xero/status');
      setXeroStatus(res.data?.data);
    } catch { /* ignore */ }
  };

  const loadClients = async () => {
    try {
      const res = await apiClient.get('/clients?limit=100');
      setClients(res.data?.data?.clients || []);
    } catch { /* ignore */ }
  };

  const handleConnectXero = async () => {
    try {
      const res = await apiClient.post('/integrations/xero/connect?returnUrl=/onboarding');
      if (res.data?.data?.authUrl) {
        // Save current step so we return here after OAuth
        sessionStorage.setItem('onboarding-step', '2');
        window.location.href = res.data.data.authUrl;
      }
    } catch {
      toast.error('Failed to connect to Xero');
    }
  };

  // Restore step after Xero OAuth redirect
  useEffect(() => {
    const savedStep = sessionStorage.getItem('onboarding-step');
    if (savedStep) {
      setStep(parseInt(savedStep));
      sessionStorage.removeItem('onboarding-step');
      // Reload Xero status and clients after redirect
      setTimeout(() => {
        loadXeroStatus();
        loadClients();
      }, 1000);
    }
  }, []);

  const saveFirmData = async () => {
    try {
      // Update tenant name
      await apiClient.put(`/tenants/${user?.tenantId}`, {
        name: firmData.name,
        settings: {
          abn: firmData.abn,
          acn: firmData.acn,
          phone: firmData.phone,
          website: firmData.website,
          timezone: firmData.timezone,
          fyEndMonth: firmData.fyEndMonth,
          address: firmData.address,
          onboarding_step: 1,
        },
      });
    } catch (err) {
      console.error('Failed to save firm data:', err);
    }
  };

  const sendInvites = async () => {
    const validInvites = invites.filter((inv) => inv.email.includes('@'));
    for (const inv of validInvites) {
      try {
        await apiClient.post('/users/invite', { email: inv.email, role: inv.role });
      } catch (err: any) {
        console.error(`Failed to invite ${inv.email}:`, err.message);
      }
    }
    if (validInvites.length > 0) {
      toast.success(`Invited ${validInvites.length} team member(s)`);
    }
  };

  const createClients = async () => {
    const validClients = newClients.filter((c) => c.name.trim());
    for (const c of validClients) {
      try {
        await apiClient.post('/clients', {
          displayName: c.name.trim(),
          abn: c.abn.trim() || undefined,
          clientType: 'company',
          status: 'active',
        });
      } catch (err: any) {
        console.error(`Failed to create client ${c.name}:`, err.message);
      }
    }
    if (validClients.length > 0) {
      toast.success(`Created ${validClients.length} client(s)`);
    }
  };

  const handleNext = async () => {
    setSaving(true);
    try {
      if (step === 0) await saveFirmData();
      if (step === 1) await sendInvites();
      // Step 2 (Xero) - no save needed, handled by OAuth
      if (step === 3) await createClients();

      if (step < STEPS.length - 1) {
        setStep(step + 1);
      } else {
        await finishOnboarding();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      finishOnboarding();
    }
  };

  const finishOnboarding = async () => {
    try {
      // Mark onboarding as completed
      await apiClient.put(`/users/${user?.id}`, { onboarding_completed: true });
      // Update tenant settings
      await apiClient.put(`/tenants/${user?.tenantId}`, {
        settings: { ...firmData, onboarding_completed: true, onboarding_completed_at: new Date().toISOString() },
      });
    } catch { /* ignore */ }
    // Full reload to refresh user data from API (onboardingCompleted flag)
    window.location.href = '/panel';
  };

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;
  const canProceed = true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <div className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#5048e5]" />
            <span className="text-sm font-medium tracking-wide">RONIDOC<span className="font-bold">AI</span></span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={() => { finishOnboarding(); }}
          >
            Skip Setup <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, idx) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'h-10 w-10 rounded-full flex items-center justify-center border-2 transition-all',
                    idx < step ? 'bg-[#5048e5] border-[#5048e5] text-white' :
                    idx === step ? 'border-[#5048e5] text-[#5048e5] bg-white dark:bg-slate-900' :
                    'border-slate-200 dark:border-slate-700 text-slate-400'
                  )}
                >
                  {idx < step ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </div>
                <span className={cn(
                  'text-[10px] mt-1.5 font-medium',
                  idx <= step ? 'text-foreground' : 'text-muted-foreground'
                )}>
                  {s.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={cn(
                  'flex-1 h-0.5 mx-2 mt-[-16px]',
                  idx < step ? 'bg-[#5048e5]' : 'bg-slate-200 dark:bg-slate-700'
                )} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">{currentStep.label}</h2>
            <p className="text-sm text-muted-foreground mt-1">{currentStep.description}</p>
          </div>

          {step === 0 && <StepFirm data={firmData} onChange={setFirmData} />}
          {step === 1 && <StepTeam invites={invites} setInvites={setInvites} />}
          {step === 2 && <StepXero xeroStatus={xeroStatus} onConnect={handleConnectXero} />}
          {step === 3 && <StepClients clients={clients} newClients={newClients} setNewClients={setNewClients} />}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              disabled={step === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>

            <div className="flex items-center gap-3">
              {step > 0 && (
                <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
                  Skip
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={saving || !canProceed}
                className="bg-[#5048e5] hover:bg-[#443fcc] min-w-[120px]"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isLastStep ? (
                  <>Finish Setup <Check className="h-4 w-4 ml-1" /></>
                ) : (
                  <>Continue <ChevronRight className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Step counter */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          Step {step + 1} of {STEPS.length}
        </p>
      </div>
    </div>
  );
}
